import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp,
  Users,
  Euro,
  Package,
  Target,
  ArrowUpRight,
  MoreVertical,
  Download,
  LineChart,
  AreaChart,
} from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { dashboardApi, salesApi, userTargetsApi } from '@/lib/api';
import { formatCurrency, formatLocalDateInput } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { StatCard } from '@/components/ui/stat-card';
import { SalesChart } from '@/components/charts/sales-chart';
import { RecentSalesTable } from '@/components/charts/recent-sales-table';
import { TopClients } from '@/components/charts/top-clients';
import { toast } from 'react-hot-toast';

const weekdayFormatter = new Intl.DateTimeFormat('es-ES', { weekday: 'long' });

function toTitleCase(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function getMonthDateRange(month: number, year: number) {
  const start = new Date(year, month - 1, 1);
  const end = new Date(year, month, 0);
  return {
    startDate: formatLocalDateInput(start),
    endDate: formatLocalDateInput(end),
  };
}

async function getSalesMetricsForRange(
  startDate: string,
  endDate: string,
): Promise<{ totalAmount: number; totalSales: number }> {
  const limit = 100;
  let page = 1;
  let totalRecords = 0;
  let aggregatedAmount = 0;

  do {
    const response = await salesApi.getAll({
      page,
      limit,
      startDate,
      endDate,
      sortBy: 'saleDate',
      sortOrder: 'DESC',
    });

    const payload = response?.data || {};
    const rows = Array.isArray(payload?.data) ? payload.data : [];
    totalRecords = Number(payload?.total || 0);
    aggregatedAmount += rows.reduce(
      (sum: number, sale: { amount: number | string }) => sum + Number(sale.amount || 0),
      0,
    );
    page += 1;
  } while ((page - 1) * limit < totalRecords);

  return {
    totalAmount: aggregatedAmount,
    totalSales: totalRecords,
  };
}

export function DashboardPage() {
  const queryClient = useQueryClient();
  const [timeRange, setTimeRange] = useState<'day' | 'week' | 'month'>('week');
  const [chartType, setChartType] = useState<'line' | 'area'>('area');
  const [chartMenuOpen, setChartMenuOpen] = useState(false);
  const [goalInput, setGoalInput] = useState('0');
  const [isSavingGoal, setIsSavingGoal] = useState(false);
  const chartMenuRef = useRef<HTMLDivElement | null>(null);

  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();

  const { data: dashboardData, isLoading: dashboardLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => dashboardApi.getDashboard(),
  });

  const { data: salesStats, isLoading: statsLoading } = useQuery({
    queryKey: ['sales-stats', timeRange],
    queryFn: () => salesApi.getStats(timeRange),
  });

  const { data: monthSalesStats } = useQuery({
    queryKey: ['sales-stats', 'month', 'quick-cards'],
    queryFn: () => salesApi.getStats('month'),
  });

  const { data: currentTargetData } = useQuery({
    queryKey: ['user-target-current', currentMonth, currentYear],
    queryFn: () => userTargetsApi.getCurrent(currentMonth, currentYear),
  });

  const targetMonth = Number(currentTargetData?.data?.month || currentMonth);
  const targetYear = Number(currentTargetData?.data?.year || currentYear);
  const { startDate: targetStartDate, endDate: targetEndDate } = getMonthDateRange(targetMonth, targetYear);
  const { startDate: currentMonthStartDate, endDate: currentMonthEndDate } = getMonthDateRange(currentMonth, currentYear);

  const { data: targetMonthMetrics = { totalAmount: 0, totalSales: 0 } } = useQuery({
    queryKey: ['sales-target-month-dashboard', targetStartDate, targetEndDate],
    queryFn: () => getSalesMetricsForRange(targetStartDate, targetEndDate),
  });

  const { data: currentMonthMetrics = { totalAmount: 0, totalSales: 0 } } = useQuery({
    queryKey: ['sales-current-month-dashboard', currentMonthStartDate, currentMonthEndDate],
    queryFn: () => getSalesMetricsForRange(currentMonthStartDate, currentMonthEndDate),
  });

  useEffect(() => {
    const targetValue = Number(currentTargetData?.data?.target || 0);
    setGoalInput(targetValue > 0 ? String(targetValue) : '0');
  }, [currentTargetData]);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (chartMenuRef.current && !chartMenuRef.current.contains(event.target as Node)) {
        setChartMenuOpen(false);
      }
    };

    window.addEventListener('mousedown', onClickOutside);
    return () => window.removeEventListener('mousedown', onClickOutside);
  }, []);

  const currentMonthName = useMemo(
    () =>
      toTitleCase(
        new Date(currentYear, currentMonth - 1, 1).toLocaleDateString('es-ES', {
          month: 'long',
        }),
      ),
    [currentMonth, currentYear],
  );

  const stats = [
    {
      title: 'Ventas Hoy',
      value: dashboardData?.data.today.count || 0,
      icon: TrendingUp,
      color: 'bg-blue-500',
      formattedValue: formatCurrency(dashboardData?.data.today.total || 0),
    },
    {
      title: `Ingresos (${currentMonthName})`,
      value: currentMonthMetrics.totalAmount || 0,
      icon: Euro,
      color: 'bg-emerald-500',
      formattedValue: formatCurrency(currentMonthMetrics.totalAmount || 0),
    },
    {
      title: `Ventas (${currentMonthName})`,
      value: currentMonthMetrics.totalSales || 0,
      icon: Package,
      color: 'bg-amber-500',
      formattedValue: currentMonthMetrics.totalSales?.toString() || '0',
    },
  ];

  const quickCards = useMemo(() => {
    const monthData = monthSalesStats?.data?.data || [];
    const bestDay = monthData.reduce(
      (max: { date: string; amount: number }, item: { date: string; amount: number }) =>
        item.amount > max.amount ? item : max,
      { date: '', amount: 0 },
    );

    const avgByActiveDay =
      monthData.length > 0
        ? monthData.reduce((sum: number, item: { amount: number }) => sum + item.amount, 0) / monthData.length
        : 0;

    const bestDayDeltaPercent =
      avgByActiveDay > 0 ? Math.round(((bestDay.amount - avgByActiveDay) / avgByActiveDay) * 100) : 0;

    const bestDayLabel = bestDay.date
      ? toTitleCase(weekdayFormatter.format(new Date(bestDay.date)))
      : 'Sin datos';

    const topClient = dashboardData?.data?.topClients?.[0] || null;

    const targetValue = Number(currentTargetData?.data?.target || 0);
    const monthRevenue = Number(targetMonthMetrics.totalAmount || 0);
    const targetProgress = targetValue > 0 ? Math.round((monthRevenue / targetValue) * 100) : 0;

    return {
      bestDayLabel,
      bestDayDeltaPercent,
      topClientName: topClient?.name || 'Sin datos',
      topClientAmount: Number(topClient?.totalSales || 0),
      targetValue,
      monthRevenue,
      targetProgress,
    };
  }, [dashboardData?.data?.topClients, currentTargetData?.data?.target, monthSalesStats?.data, targetMonthMetrics.totalAmount]);

  const targetMonthName = useMemo(
    () =>
      toTitleCase(
        new Date(targetYear, targetMonth - 1, 1).toLocaleDateString('es-ES', {
          month: 'long',
        }),
      ),
    [targetMonth, targetYear],
  );

  const targetValue = Number(currentTargetData?.data?.target || 0);
  const monthRevenue = Number(targetMonthMetrics.totalAmount || 0);
  const targetProgress = targetValue > 0 ? (monthRevenue / targetValue) * 100 : 0;
  const target150Value = targetValue * 1.5;
  const remainingTo100 = Math.max(0, targetValue - monthRevenue);
  const remainingTo150 = Math.max(0, target150Value - monthRevenue);
  const firstSegmentWidth = targetValue > 0 ? (Math.min(targetProgress, 100) / 150) * 100 : 0;
  const secondSegmentWidth = targetValue > 0 ? (Math.max(Math.min(targetProgress - 100, 50), 0) / 150) * 100 : 0;
  const targetStatusText =
    targetValue <= 0
      ? null
      : targetProgress >= 150
        ? 'Objetivo 150% superado'
        : targetProgress >= 100
          ? `Falta para 150%: ${formatCurrency(remainingTo150)}`
          : `Falta para 100%: ${formatCurrency(remainingTo100)} · Falta para 150%: ${formatCurrency(remainingTo150)}`;

  const saveMonthlyGoal = async () => {
    const parsed = Number(goalInput);
    if (!Number.isFinite(parsed) || parsed < 0) {
      toast.error('Introduce un objetivo válido.');
      return;
    }

    setIsSavingGoal(true);
    try {
      await userTargetsApi.upsertCurrent({
        target: parsed,
        month: targetMonth,
        year: targetYear,
      });
      toast.success('Objetivo actualizado.');
      queryClient.invalidateQueries({
        queryKey: ['user-target-current', currentMonth, currentYear],
      });
    } catch (error) {
      console.error('Failed to update monthly goal', error);
      toast.error('No se pudo actualizar el objetivo.');
    } finally {
      setIsSavingGoal(false);
    }
  };

  const handleExportCsv = () => {
    const rows = salesStats?.data?.data || [];

    if (!rows.length) {
      toast.error('No hay datos para exportar en este periodo.');
      return;
    }

    const header = ['Fecha', 'Importe'];
    const csvLines = [header.join(';')];

    for (const row of rows) {
      const date = new Date(row.date).toLocaleDateString('es-ES');
      const amount = Number(row.amount || 0).toFixed(2).replace('.', ',');
      csvLines.push([date, amount].join(';'));
    }

    const total = Number(salesStats?.data?.total || 0).toFixed(2).replace('.', ',');
    csvLines.push('');
    csvLines.push(['Total periodo', total].join(';'));

    const blob = new Blob([`\uFEFF${csvLines.join('\n')}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `dashboard-${timeRange}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    toast.success('CSV exportado correctamente.');
  };

  if (dashboardLoading || statsLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="h-8 w-48 bg-gray-200 dark:bg-gray-800 rounded-lg animate-pulse"></div>
          <div className="h-10 w-32 bg-gray-200 dark:bg-gray-800 rounded-lg animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-gray-200 dark:bg-gray-800 rounded-xl animate-pulse"></div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 h-96 bg-gray-200 dark:bg-gray-800 rounded-xl animate-pulse"></div>
          <div className="h-96 bg-gray-200 dark:bg-gray-800 rounded-xl animate-pulse"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Resumen de ventas y estadísticas</p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
            {(['day', 'week', 'month'] as const).map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  timeRange === range
                    ? 'bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                {range === 'day' ? 'Hoy' : range === 'week' ? 'Semana' : 'Mes'}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={handleExportCsv}>
            <Download className="w-4 h-4 mr-2" />
            Exportar
          </Button>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 items-stretch gap-6 md:grid-cols-2 lg:grid-cols-5">
        {stats.slice(0, 1).map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="h-full"
          >
            <StatCard {...stat} className="min-h-[255px]" />
          </motion.div>
        ))}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="h-full rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900 lg:col-span-2"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Objetivo de {targetMonthName}
              </p>
              <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                {targetValue > 0 ? `${targetProgress.toFixed(1)}%` : 'Sin objetivo'}
              </p>
            </div>
            <div className="rounded-xl bg-amber-500/10 p-3">
              <Target className="h-6 w-6 text-amber-500" />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <input
              type="number"
              min="0"
              step="0.01"
              value={goalInput}
              onChange={(event) => setGoalInput(event.target.value)}
              className="h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder="Ej: 20000"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={saveMonthlyGoal}
              loading={isSavingGoal}
            >
              Guardar
            </Button>
          </div>

          <div className="mt-5">
            <div className="relative mb-2 h-4 text-[11px] tracking-wide text-gray-500 dark:text-gray-400">
              <span className="absolute left-0">0%</span>
              <span className="absolute left-2/3 -translate-x-1/2">100%</span>
              <span className="absolute right-0">150%</span>
            </div>
            <div className="relative h-2.5 overflow-hidden rounded-full bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800">
              <div
                className={`absolute inset-y-0 left-0 rounded-l-full transition-all duration-500 ${targetProgress < 100 ? 'bg-gradient-to-r from-amber-500 to-orange-600' : 'bg-gradient-to-r from-emerald-500 to-green-600'}`}
                style={{ width: `${firstSegmentWidth}%` }}
              />
              <div
                className={`absolute inset-y-0 left-2/3 transition-all duration-500 ${targetProgress >= 150 ? 'bg-gradient-to-r from-blue-500 to-sky-600' : 'bg-gradient-to-r from-emerald-500 to-green-600'}`}
                style={{ width: `${secondSegmentWidth}%` }}
              />
              <div className="pointer-events-none absolute inset-y-0 left-2/3 w-px bg-white/80 dark:bg-gray-900/80" />
            </div>
          </div>


          {targetStatusText && (
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
              <strong>{targetStatusText}</strong>
            </p>
          )}
        </motion.div>
        {stats.slice(1).map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: (index + 2) * 0.1 }}
            className="h-full"
          >
            <StatCard {...stat} className="min-h-[255px]" />
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="lg:col-span-2"
        >
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Ventas {timeRange === 'day' ? 'de Hoy' : timeRange === 'week' ? 'de la Semana' : 'del Mes'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Evolución de ventas en el tiempo</p>
              </div>
              <div className="relative" ref={chartMenuRef}>
                <button
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                  onClick={() => setChartMenuOpen((prev) => !prev)}
                  aria-label="Opciones de gráfica"
                >
                  <MoreVertical className="w-5 h-5 text-gray-500" />
                </button>

                {chartMenuOpen && (
                  <div className="absolute right-0 top-10 z-20 w-44 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-800 dark:bg-gray-900">
                    <button
                      onClick={() => {
                        setChartType('line');
                        setChartMenuOpen(false);
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <LineChart className="h-4 w-4" />
                      Vista línea
                    </button>
                    <button
                      onClick={() => {
                        setChartType('area');
                        setChartMenuOpen(false);
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <AreaChart className="h-4 w-4" />
                      Vista área
                    </button>
                    <button
                      onClick={() => {
                        handleExportCsv();
                        setChartMenuOpen(false);
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <Download className="h-4 w-4" />
                      Exportar CSV
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="h-80">
              <SalesChart
                data={salesStats?.data?.data || []}
                chartType={chartType}
                onChartTypeChange={setChartType}
                showTypeSelector={true}
              />
            </div>
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-800">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total del periodo</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(salesStats?.data?.total || 0)}
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Promedio por venta</p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(salesStats?.data?.average || 0)}
                    </p>
                  </div>
                  <div className="flex items-center text-emerald-600 dark:text-emerald-400">
                    <ArrowUpRight className="w-5 h-5 mr-1" />
                    <span className="font-semibold">+{salesStats?.data?.count || 0} ventas</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 shadow-sm h-full">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Top Clientes</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Por volumen de ventas</p>
              </div>
              <Button variant="ghost" size="sm">
                Ver todos
              </Button>
            </div>
            <TopClients clients={dashboardData?.data?.topClients || []} />
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Ventas Recientes</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Últimas ventas registradas</p>
            </div>
            <Button variant="outline" size="sm">
              Ver todas
            </Button>
          </div>
          <RecentSalesTable sales={dashboardData?.data?.recentSales || []} />
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-6"
      >
        <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Mejor día</p>
              <p className="text-2xl font-bold mt-1">{quickCards.bestDayLabel}</p>
              <p className="text-sm opacity-90 mt-2">
                {quickCards.bestDayDeltaPercent >= 0 ? '+' : ''}
                {quickCards.bestDayDeltaPercent}% sobre promedio
              </p>
            </div>
            <TrendingUp className="w-12 h-12 opacity-80" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Cliente estrella</p>
              <p className="text-2xl font-bold mt-1">{quickCards.topClientName}</p>
              <p className="text-sm opacity-90 mt-2">{formatCurrency(quickCards.topClientAmount)} este mes</p>
            </div>
            <Users className="w-12 h-12 opacity-80" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Objetivo de {targetMonthName}</p>
              <p className="text-2xl font-bold mt-1">
                {quickCards.targetValue > 0 ? `${quickCards.targetProgress}%` : 'Sin objetivo'}
              </p>
              <p className="text-sm opacity-90 mt-2">
                {quickCards.targetValue > 0
                  ? `${formatCurrency(quickCards.monthRevenue)} de ${formatCurrency(quickCards.targetValue)}`
                  : `${formatCurrency(quickCards.monthRevenue)} acumulado`}
              </p>
              <p className="text-xs opacity-80 mt-1">Mes objetivo: {targetMonthName}</p>
            </div>
            <Package className="w-12 h-12 opacity-80" />
          </div>
        </div>
      </motion.div>
    </div>
  );
}
